'use client';

import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { Heading } from '../ui/heading';
import { Paragraph } from '../ui/paragraph';
import { getDefaultUrlForTosOrGdprLocalized } from '@eliadmin/core/src/utils/misc-utils';
import Link from '@eliadmin/core/src/components/link';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { IoMdPaperPlane } from 'react-icons/io';

export function Newsletter() {
  const systemLabels = useSystemLabels();
  const { t } = useTranslations();

  // state
  const [email, setEmail] = React.useState('');
  const [checked, setChecked] = React.useState(true);
  const [isSubscribed, setIsSubscribed] = React.useState(false);
  const [isAlreadySubscribed, setIsAlreadySubscribed] = React.useState(false);

  const subscribeMutation = useMutation({
    mutationFn: async (
      request: Parameters<
        (typeof import('@eliadmin/core/src/api/newsletter/newsletter-api'))['newsletterSubscribe']
      >[0],
    ) => {
      const { newsletterSubscribe } =
        await import('@eliadmin/core/src/api/newsletter/newsletter-api');
      return newsletterSubscribe(request);
    },
    onSuccess: () => setIsSubscribed(true),
    onError: () => setIsAlreadySubscribed(true),
  });

  const locale = useLocaleStore(s => s.currentLocale);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!checked || isSubscribed || !email || subscribeMutation.isPending) return;
    subscribeMutation.mutate({
      locale,
      body: {
        email,
        gdpr: checked,
        return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
      },
    });
  }

  const GDPRhref = getDefaultUrlForTosOrGdprLocalized(ROUTES?.GDPR?.() ?? '/', locale, 'gdpr');

  return (
    <form
      className={cn(
        'custom-container',
        'relative flex flex-col items-start justify-start gap-6 overflow-hidden bg-[#181818] p-6 pb-52 sm:p-8 sm:pb-64 md:flex-row lg:p-10 lg:pb-10 xl:p-12 xl:pb-12',
        'ea-homepage-newsletter__form',
      )}
      onSubmit={handleSubmit}
    >
      <div className="flex w-full flex-col items-start justify-start gap-4 sm:gap-5 md:max-w-[642px] lg:gap-6">
        <div className="relative z-10 flex w-full flex-col items-start justify-start gap-2">
          <Heading title={t('text3462')} tag="h3" as="h2" className={{ title: 'text-white' }} />
          <Paragraph className="tracking-tight" color="white">
            {t('text3463')}
          </Paragraph>
        </div>
        <div className="flex w-full items-center justify-start gap-2 lg:gap-3">
          <Input
            aria-label={systemLabels('email')}
            id="newsletter-email"
            type="email"
            name="email"
            rounded={'none'}
            placeholder={t('text3464')}
            onChange={e => setEmail(e.target.value)}
            className={{
              wrapper: cn(
                'h-[52px] w-full rounded-none bg-[#F5F5F5] sm:h-14 md:max-w-[300px] lg:h-[60px]',
                'ea-homepage-newsletter__form-item-email-input',
              ),
              main: cn(
                'h-[52px] rounded-none bg-[#F5F5F5] px-4 sm:h-14 lg:h-[60px]',
                'ea-homepage-newsletter__form-item-email-input-main',
              ),
            }}
          />
          <Button
            type="submit"
            className={cn(
              'hidden h-[52px] w-fit min-w-[52px] max-w-[52px] whitespace-nowrap sm:flex sm:h-14 sm:min-w-max sm:max-w-max lg:h-[60px]',
              'ea-homepage-newsletter__form-item-email-submit-button',
            )}
            disabled={email.length === 0 || !checked || isSubscribed || subscribeMutation.isPending}
            isLoading={subscribeMutation.isPending}
          >
            {t('text182')}
          </Button>

          <Button
            type="submit"
            aria-label={t('text182')}
            className={cn(
              'flex h-[52px] min-w-[52px] max-w-[52px] p-0 sm:hidden',
              'ea-homepage-newsletter__form-item-email-submit-button',
            )}
            disabled={email.length === 0 || !checked || isSubscribed || subscribeMutation.isPending}
            isLoading={subscribeMutation.isPending}
          >
            <IoMdPaperPlane size={24} className="max-h-6 min-h-6 min-w-6 max-w-6 text-white" />
          </Button>
        </div>
        <Paragraph className="w-full tracking-tight text-white/70 md:max-w-[420px]  xl:max-w-max">
          {t('text613')}{' '}
          <Link
            prefetch={false}
            target="_blank"
            href={GDPRhref}
            className="underline transition-all hover:opacity-80"
          >
            {t('text614')}
          </Link>
          &nbsp;
          {t('text2402')}
        </Paragraph>
      </div>
      <Image
        src={'/images/newsletter.png'}
        alt="Newsletter"
        width={0}
        height={0}
        className="absolute bottom-0 left-1/2 right-0 z-[3] h-[199px] w-auto -translate-x-1/2 object-contain object-center sm:h-[240px] md:left-auto md:h-full md:translate-x-0 lg:right-12"
        sizes="100vw"
      />
      <div className={cn('ea-newsletter__image-blur')} />
    </form>
  );
}
